import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { API_BASE } from '@/lib/api-base';
import { HrmsFieldPicker, type HrmsFieldOption } from '@/components/HrmsFieldPicker';

/**
 * "Match your HRMS fields" — the onboarding wizard's field-mapping form.
 *
 * The employee import refuses to run on a guessed mapping, and it is right to:
 * a `date_of_joining` that is really the confirmation date changes proration,
 * arrears and FnF for everyone, and nothing looks wrong until a payslip is
 * questioned months later. But the block used to have no way out inside the
 * product — the mapping could only be confirmed from a server shell, so every
 * new instance stalled here until someone with SSH noticed.
 *
 * Nothing in here is instance-specific. The payroll side (the rows) comes
 * from the server's GUESS_RULES; the HRMS side (the options) is read live from
 * that instance's own `getProfileFields` on every load, plus the uKnowva
 * account fields every `getUsers` row carries — cached only as an outage
 * fallback. So the same form works for the next client without a line of
 * code changing, and a field added in uKnowva appears on the next reload.
 */

interface FieldRow {
  payroll_field: string;
  label: string;
  note: string;
  required: boolean;
  hrms_field: string | null;
  hrms_label: string | null;
  hrms_type: string;
  missing_on_instance: boolean;
  confidence: string | null;
  status: 'confirmed' | 'auto' | 'unmapped';
}

interface FieldMapResponse {
  request_id: string;
  instance: string;
  confirmed: boolean;
  fields: FieldRow[];
  available: HrmsFieldOption[];
  ready: string[];
  unmapped_required: string[];
  problems: string[];
}

export function FieldMapPanel({
  requestId,
  onSaved,
}: {
  requestId: string;
  onSaved: () => void;
}) {
  const [data, setData] = useState<FieldMapResponse | null>(null);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await fetch(`${API_BASE}/provisioning/${requestId}/field-map`);
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const body = (await res.json()) as FieldMapResponse;
      setData(body);
      // Seed from what the server already has — confirmed first, then its
      // guess — so the common case is "read it, press Save".
      setChoices(
        Object.fromEntries(
          body.fields.map((f) => [f.payroll_field, f.hrms_field ?? '']),
        ),
      );
    } catch (err) {
      setLoadError((err as Error).message);
    }
  }, [requestId]);

  useEffect(() => {
    void load();
  }, [load]);

  // One HRMS field must not feed two payroll fields: getUsers returns it once,
  // and the duplicate is nearly always a mis-click. The server refuses it too;
  // catching it here means the user sees which two rows clash.
  const duplicates = useMemo(() => {
    const seen = new Map<string, string>();
    const clashing = new Set<string>();
    for (const [payrollField, code] of Object.entries(choices)) {
      if (!code) continue;
      const first = seen.get(code);
      if (first) {
        clashing.add(first);
        clashing.add(payrollField);
      } else {
        seen.set(code, payrollField);
      }
    }
    return clashing;
  }, [choices]);

  const missingRequired = useMemo(
    () =>
      (data?.fields ?? []).filter((f) => f.required && !choices[f.payroll_field]),
    [data, choices],
  );

  const willImport = useMemo(
    () => (data?.fields ?? []).filter((f) => choices[f.payroll_field]),
    [data, choices],
  );

  // code → payroll field label, so the picker can say "already used for …".
  const takenBy = useMemo(() => {
    const labels = Object.fromEntries((data?.fields ?? []).map((f) => [f.payroll_field, f.label]));
    const out: Record<string, string> = {};
    for (const [payrollField, code] of Object.entries(choices)) {
      if (code) out[code] = labels[payrollField] ?? payrollField;
    }
    return out;
  }, [data, choices]);

  const save = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const res = await fetch(`${API_BASE}/provisioning/${requestId}/field-map`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mapping: choices }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { detail?: string };
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      onSaved();
    } catch (err) {
      setSaveError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loadError) {
    return (
      <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void load()}>
              Retry
            </Button>
          }
        >
          Could not read your uKnowva profile fields. {loadError}
        </Alert>
      </Paper>
    );
  }

  if (!data) {
    return (
      <Paper variant="outlined" sx={{ mt: 2, p: 3, textAlign: 'center' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Reading the fields your uKnowva exposes…
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ mt: 2, p: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Match your uKnowva fields to payroll
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every uKnowva names its profile fields differently, so payroll cannot
        assume which one holds an employee code or a joining date — and a wrong
        guess changes what people are paid. Check the matches below and confirm
        them once; imports from then on are automatic.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color="success"
          variant="outlined"
          label={`${willImport.length} of ${data.fields.length} fields will import`}
        />
        <Chip
          size="small"
          color={missingRequired.length ? 'error' : 'default'}
          variant="outlined"
          label={
            missingRequired.length
              ? `${missingRequired.length} required field(s) still to pick`
              : 'All required fields matched'
          }
        />
        <Chip
          size="small"
          variant="outlined"
          label={`${data.available.length} fields available in uKnowva`}
        />
      </Stack>

      {missingRequired.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Employees cannot be imported until{' '}
          {missingRequired.map((f) => f.label).join(', ')}{' '}
          {missingRequired.length === 1 ? 'is' : 'are'} matched. Pick the uKnowva
          field that holds each one below.
        </Alert>
      )}

      <Stack divider={<Divider flexItem />} spacing={0}>
        {data.fields.map((row) => {
          const chosen = choices[row.payroll_field] ?? '';
          const clash = duplicates.has(row.payroll_field);
          return (
            <Box
              key={row.payroll_field}
              sx={{
                display: 'flex',
                gap: 2,
                py: 1.25,
                alignItems: 'flex-start',
                flexWrap: { xs: 'wrap', sm: 'nowrap' },
              }}
            >
              <Box sx={{ minWidth: 190, flexShrink: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Typography variant="body2">{row.label}</Typography>
                  {row.required && (
                    <Chip size="small" color="error" variant="outlined" label="required" />
                  )}
                </Stack>
                {row.note && (
                  <Typography variant="caption" color="text.secondary">
                    {row.note}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: 1, minWidth: 240 }}>
                <HrmsFieldPicker
                  value={chosen}
                  options={data.available}
                  required={row.required}
                  error={clash || (row.required && !chosen)}
                  takenBy={takenBy}
                  onChange={(code) =>
                    setChoices((c) => ({ ...c, [row.payroll_field]: code }))
                  }
                />
                <Typography
                  variant="caption"
                  color={clash ? 'error' : 'text.secondary'}
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {clash
                    ? 'This uKnowva field is already used by another row'
                    : row.missing_on_instance && chosen === row.hrms_field
                      ? 'This field is no longer in your uKnowva — pick its replacement'
                      : !chosen
                      ? row.required
                        ? 'Required — the import cannot run without it'
                        : 'Left empty in payroll; you can fill it in later'
                      : row.status === 'auto' && chosen === row.hrms_field
                        ? `Auto-matched${row.confidence ? ` (${row.confidence} confidence)` : ''} — please confirm`
                        : 'Will be imported from this field'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Stack>

      {data.problems.length > 0 && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          {data.problems.join('; ')}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {saveError}
        </Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || missingRequired.length > 0 || duplicates.size > 0}
        >
          {saving ? 'Saving…' : 'Confirm and import employees'}
        </Button>
        <Typography variant="caption" color="text.secondary">
          You can change this later — re-importing updates identity and org
          placement only, never salary or status.
        </Typography>
      </Stack>
    </Paper>
  );
}
